// Product recommendation flow — aligned to LK_ProgramMapper / AI_App_Context router inputs
import { needsCountyDisambiguation } from '../utils/propertyLocationLookup';

export const getQuestionText = (question, loanPurpose) => {
  if (question.questionByPurpose && loanPurpose) {
    return question.questionByPurpose[loanPurpose] || question.question;
  }
  return question.question;
};

export const getEducationContent = (question, loanPurpose) => {
  if (question.educationByPurpose && loanPurpose) {
    return question.educationByPurpose[loanPurpose] || question.education;
  }
  return question.education;
};

export const getQuestionSubtitle = (question, loanPurpose) => {
  if (question?.subtitleByPurpose && loanPurpose) {
    return question.subtitleByPurpose[loanPurpose] || question.subtitle;
  }
  return question?.subtitle;
};

export const getQuestionPlaceholder = (question, loanPurpose) => {
  if (question?.placeholderByPurpose && loanPurpose) {
    return question.placeholderByPurpose[loanPurpose] || question.placeholder;
  }
  return question?.placeholder;
};

export const RECOMMENDATION_QUESTIONS = [
  // ── Phase 1: router essentials ─────────────────────────────
  {
    id: 'purchaseOrRefinance',
    question: 'Are you buying a home, or refinancing a mortgage you already have?',
    subtitle: 'There are no wrong answers — pick what you are mainly trying to do.',
    type: 'radio',
    options: [
      { value: 'purchase', label: 'I am buying a home', iconClass: 'ti-home' },
      { value: 'refinance', label: 'I am refinancing my current mortgage', iconClass: 'ti-refresh' },
    ],
    education: {
      title: 'Buying vs refinancing',
      description: 'Buying means getting a new loan to purchase a home. Refinancing means replacing your existing home loan with a new one.',
      details: [
        'If you are buying, I will ask about price, down payment, and how you will use the home.',
        'If you are refinancing, I will ask about your current loan and what you want to change.',
      ],
    },
  },
  {
    id: 'refiGoal',
    question: 'What do you mainly want from your refinance?',
    subtitle: 'Choose the closest match — you can tap the help icon on any option if you are unsure.',
    type: 'radio',
    showIf: (a) => a.purchaseOrRefinance === 'refinance',
    options: [
      { value: 'rate-term', label: 'Lower my rate or monthly payment (no cash back)', iconClass: 'ti-trending-down' },
      { value: 'limited-cash-out', label: 'Take out a small amount of cash at closing', iconClass: 'ti-cash' },
      { value: 'cash-out', label: 'Take out a larger amount of cash from my equity', iconClass: 'ti-coin' },
      { value: 'usda-streamline', label: 'I already have a USDA loan and want to refinance it', iconClass: 'ti-leaf' },
      { value: 'not-sure', label: 'I am not sure yet', iconClass: 'ti-help' },
    ],
    education: {
      title: 'Refinance goals',
      description: 'Refinancing can mean lowering your payment, changing your loan term, or borrowing against your home equity.',
      details: [
        '“No cash back” means you are not trying to pull money out of the home.',
        'Cash-out means you want to receive money at closing based on equity you have built.',
      ],
    },
  },
  {
    id: 'propertyLocation',
    question: 'Where is the home located?',
    subtitle: 'Start with the ZIP code if you know it — otherwise you can enter state and county.',
    type: 'propertyLocation',
    showIf: (a) => !(a.propertyState && a.propertyCounty),
  },
  {
    id: 'propertyCounty',
    question: 'Which county is the home in?',
    subtitle: 'Your ZIP code crosses more than one county — choose where the property actually sits.',
    type: 'countySelect',
    showIf: (a) => needsCountyDisambiguation(a),
  },
  {
    id: 'propertyUse',
    question: 'How will you use this home?',
    questionByPurpose: {
      purchase: 'How do you plan to use this home after you buy it?',
      refinance: 'How do you use this home today?',
    },
    subtitle: 'This affects which loan types may be available.',
    type: 'radio',
    options: [
      { value: 'primary', label: 'I will live there as my main home', iconClass: 'ti-home' },
      { value: 'secondary', label: 'Vacation or second home', iconClass: 'ti-beach' },
      { value: 'investment', label: 'Rental or investment property', iconClass: 'ti-chart-line' },
    ],
    education: {
      title: 'How you use the home',
      description: 'Lenders treat main homes, second homes, and rentals differently for rates and requirements.',
      details: [
        'Most assistance programs are for homes you will live in full time.',
        'Investment properties often require more money down and stricter rules.',
      ],
    },
  },
  {
    id: 'estimatedValue',
    question: 'About how much is the home worth?',
    questionByPurpose: {
      purchase: 'About how much do you expect to pay for the home?',
      refinance: 'About how much do you think your home is worth today?',
    },
    subtitle: 'A rough estimate is fine — you can change it later.',
    type: 'currency',
    placeholder: '500,000',
    education: {
      title: 'Home value',
      description: 'This helps estimate your loan size and monthly payment. It does not need to be exact.',
      details: [
        'For a purchase, use the price you expect to offer or pay.',
        'For a refinance, use your best guess of current market value.',
      ],
    },
  },
  {
    id: 'downPayment',
    question: 'How much cash are you putting toward the purchase?',
    questionByPurpose: {
      purchase: 'How much cash are you putting toward the purchase at closing?',
      refinance: 'About how much do you still owe on your mortgage?',
    },
    subtitleByPurpose: {
      purchase: 'This is your down payment — money you pay upfront, not the loan amount.',
      refinance: 'Find this on your latest mortgage statement as principal balance or payoff amount.',
    },
    type: 'currency',
    placeholder: '100,000',
    placeholderByPurpose: {
      purchase: '100,000',
      refinance: '350,000',
    },
    education: {
      title: 'Down payment or payoff',
      description: 'For a purchase, this is cash you bring to closing. For a refinance, this is how much you still owe.',
      details: [
        'Larger down payments can mean a smaller loan and sometimes a lower rate.',
        'For refinances, I use your payoff and home value to estimate your loan size.',
      ],
    },
    educationByPurpose: {
      refinance: {
        title: 'Mortgage balance',
        description: 'Enter the amount you still owe on your current home loan — not a new down payment.',
        details: [
          'If you want cash back at closing, you told me that in your refinance goal.',
          'Your balance compared to home value helps show how much equity you have.',
        ],
      },
    },
  },
  {
    id: 'creditScore',
    question: 'What is your best estimate of your credit score?',
    subtitle: 'Self-reported only — this is not a credit pull. Tap the help icon if you are unsure.',
    type: 'creditScore',
    placeholder: 'e.g. 720',
    education: {
      title: 'Credit score',
      description: 'Your credit score affects pricing and which loan types may fit. Many people check their score in a banking app or at annualcreditreport.com.',
      details: [
        'Enter your best estimate as three digits. Tap help to see the range I accept.',
      ],
    },
  },
  {
    id: 'loanSizeTier',
    internal: true,
    showIf: () => false,
    question: 'Where does your loan amount likely fall?',
    subtitle: 'Use your estimate: loan amount vs. baseline conforming limit in your county (workbook uses FHFA baseline + high-cost caps).',
    type: 'radio',
    options: [
      { value: 'conforming', label: 'At or below baseline conforming', iconClass: 'ti-chart-bar', description: 'Typical conforming / RefiNow-eligible band' },
      { value: 'high_balance', label: 'High-balance / super conforming', iconClass: 'ti-chart-line', description: 'Above baseline, within agency high-cost limit' },
      { value: 'jumbo', label: 'Above agency limits (jumbo / non-agency)', iconClass: 'ti-building-skyscraper', description: 'Portfolio / non-QM territory' },
    ],
    education: {
      title: 'Loan size tier',
      description: 'The router tags rows with countyLimitCategory and highLoanLimitAllowed. Your choice filters conforming, high-balance, and jumbo buckets.',
      details: [
        'RefiNow and Refi Possible require general conforming positioning (no high-balance) per mapper notes.',
        'High-balance conventional rows exclude baseline-only refi specials.',
        'Jumbo full-doc and several non-QM paths are for above-agency-limit balances.',
      ],
    },
  },

  // ── Phase 2: gates + preferences ───────────────────────────
  {
    id: 'propertyType',
    question: 'What type of home is it?',
    questionByPurpose: {
      purchase: 'What type of home are you buying?',
      refinance: 'What type of home are you refinancing?',
    },
    type: 'radio',
    options: [
      { value: 'single-family', label: 'Detached house (stand-alone home)', iconClass: 'ti-home' },
      { value: 'condo', label: 'Condominium', iconClass: 'ti-building' },
      { value: 'townhouse', label: 'Townhouse', iconClass: 'ti-home-2' },
      { value: 'multi-family', label: 'Building with 2–4 units', iconClass: 'ti-building-community' },
    ],
    education: {
      title: 'Type of home',
      description: 'Houses, condos, and townhomes can qualify for different loan options.',
      details: [
        'Condos sometimes need extra lender approval for the condo project.',
      ],
    },
  },
  {
    id: 'militaryService',
    question: 'Have you served in the U.S. military, or are you the surviving spouse of someone who did?',
    subtitle: 'Veterans may qualify for special home loan benefits with low or no down payment.',
    type: 'radio',
    options: [
      { value: 'yes', label: 'Yes', iconClass: 'ti-military-rank' },
      { value: 'no', label: 'No', iconClass: null },
    ],
    education: {
      title: 'Military service',
      description: 'Active duty, veterans, and some surviving spouses may qualify for VA-backed home loans.',
      details: [
        'You will need proof of eligibility when you work with a lender — we are just preparing for now.',
      ],
    },
  },
  {
    id: 'ruralProperty',
    question: 'Is the home in a small town or rural area (not a major city)?',
    subtitle: 'Some loans are only for homes outside dense urban areas.',
    type: 'radio',
    options: [
      { value: 'yes', label: 'Yes, small town or rural', iconClass: 'ti-plant' },
      { value: 'no', label: 'No, in a city or suburb', iconClass: 'ti-building-skyscraper' },
      { value: 'unsure', label: 'I am not sure', iconClass: 'ti-help' },
    ],
    education: {
      title: 'Rural location',
      description: 'USDA loans can help buyers in eligible rural and suburban areas, often with no down payment.',
      details: [
        'If you are not sure, pick “I am not sure” — we can check the address later.',
      ],
    },
  },
  {
    id: 'existingUSDALoan',
    question: 'Is your current mortgage a USDA loan?',
    subtitle: 'Only applies if you selected a USDA refinance path earlier.',
    type: 'radio',
    showIf: (a) =>
      a.purchaseOrRefinance === 'refinance' &&
      a.refiGoal === 'usda-streamline' &&
      a.ruralProperty === 'yes',
    options: [
      { value: 'yes', label: 'Yes', iconClass: 'ti-check' },
      { value: 'no', label: 'No', iconClass: 'ti-x' },
    ],
    education: {
      title: 'USDA loan today',
      description: 'This path is for people who already have a USDA loan and want to refinance it.',
      details: [],
    },
  },
  {
    id: 'priorNADL',
    question: 'Are you refinancing a Native American Direct Loan you already have?',
    subtitle: 'This is a specialized program for eligible veterans on tribal trust land.',
    type: 'radio',
    showIf: (a) => a.purchaseOrRefinance === 'refinance',
    options: [
      { value: 'yes', label: 'Yes', iconClass: 'ti-check' },
      { value: 'no', label: 'No', iconClass: 'ti-x' },
    ],
    education: {
      title: 'Native American Direct Loan',
      description: 'Most people answer no. Only choose yes if you already have this specific VA tribal housing loan.',
      details: [],
    },
  },
  {
    id: 'stateHfaParticipant',
    question: 'Is a state or local affordable homeownership program already helping you with this loan?',
    subtitle: 'Only answer yes if a housing counselor or loan officer already told you you are in one of those programs.',
    type: 'radio',
    showIf: (a) => a.propertyUse === 'primary',
    options: [
      { value: 'yes', label: 'Yes — I am already in that kind of program', iconClass: 'ti-building-bank' },
      { value: 'no', label: 'No — I am not using one of those programs', iconClass: null },
    ],
    education: {
      title: 'Affordable housing programs',
      description: 'Some states offer down-payment help or lower rates through housing finance agencies. Most buyers are not in one yet.',
      details: [
        'If no one has mentioned a state program to you, choose “No.”',
      ],
    },
  },
  {
    id: 'tribalHousingPath',
    question: 'Are you using a special home loan program for tribal members?',
    subtitle: 'Most people choose the first option unless a tribal housing office is already helping you.',
    type: 'radio',
    showIf: (a) => a.propertyUse === 'primary',
    options: [
      { value: 'none', label: 'No — standard home loan', iconClass: 'ti-minus' },
      { value: 'section184', label: 'Yes — Section 184 (tribal member program)', iconClass: 'ti-flag' },
      { value: 'nadl', label: 'Yes — veteran on tribal trust land', iconClass: 'ti-shield' },
    ],
    education: {
      title: 'Tribal housing programs',
      description: 'Section 184 and similar programs serve enrolled tribal members in eligible areas. NADL is for eligible veterans on tribal trust land.',
      details: [
        'If you are not sure, choose “No — standard home loan.”',
      ],
    },
  },
  {
    id: 'affordableProgramInterest',
    question: 'Would you like me to include programs for buyers with moderate incomes?',
    subtitle: 'These programs can offer help if your income is within certain limits for your area.',
    type: 'radio',
    showIf: (a) => a.purchaseOrRefinance === 'purchase' && a.propertyUse === 'primary',
    options: [
      { value: 'yes', label: 'Yes — show me those options', iconClass: 'ti-heart' },
      { value: 'no', label: 'No — stick with standard options', iconClass: null },
    ],
    education: {
      title: 'Income-based programs',
      description: 'Some loans have income caps based on typical earnings in your area. They can mean lower rates or down-payment help.',
      details: [
        'You can always say no and still see other loan types.',
      ],
    },
  },
  {
    id: 'firstTimeHomeBuyer',
    question: 'Is this the first home you have owned in the last few years?',
    subtitle: 'First-time buyers sometimes qualify for extra assistance.',
    type: 'radio',
    showIf: (a) => a.purchaseOrRefinance === 'purchase',
    options: [
      { value: 'yes', label: 'Yes — first-time buyer', iconClass: 'ti-confetti' },
      { value: 'no', label: 'No — I have owned before', iconClass: 'ti-home' },
    ],
    education: {
      title: 'First-time buyer',
      description: '“First-time” usually means you have not owned a home in the past three years, even if the rules vary by program.',
      details: [],
    },
  },
  {
    id: 'renovationNeeds',
    question: 'Do you need money for repairs or upgrades included in the loan?',
    type: 'radio',
    options: [
      { value: 'none', label: 'No — the home is fine as-is', iconClass: 'ti-check' },
      { value: 'minor', label: 'Yes — smaller updates or cosmetic work', iconClass: 'ti-paint' },
      { value: 'major', label: 'Yes — major repairs or renovation', iconClass: 'ti-tools' },
      { value: 'not-sure', label: 'Not sure yet', iconClass: 'ti-help' },
    ],
    education: {
      title: 'Renovation financing',
      description: 'Some loans let you borrow extra for repairs as part of the mortgage. Others are for move-in ready homes only.',
      details: [
        'Choose “No” if you are not planning construction or major rehab.',
      ],
    },
  },
  {
    id: 'agencyLoanServicer',
    question: 'Do you know whether your current loan is backed by Fannie Mae or Freddie Mac?',
    subtitle: 'Many people do not know — that is okay. Choose “Other / not sure” if you are unsure.',
    type: 'radio',
    showIf: (a) =>
      a.purchaseOrRefinance === 'refinance' &&
      ['rate-term', 'not-sure', 'limited-cash-out'].includes(a.refiGoal) &&
      a.propertyUse === 'primary',
    options: [
      { value: 'fannie', label: 'Fannie Mae (or I was told Fannie)', iconClass: 'ti-building' },
      { value: 'freddie', label: 'Freddie Mac (or I was told Freddie)', iconClass: 'ti-building' },
      { value: 'other', label: 'Other, my bank, or not sure', iconClass: 'ti-help' },
    ],
    education: {
      title: 'Who owns your loan',
      description: 'Fannie Mae and Freddie Mac are large companies that buy many U.S. mortgages. Your monthly statement may not say their name — “not sure” is common.',
      details: [
        'A loan officer can confirm this later from your loan documents.',
      ],
    },
  },
  {
    id: 'refiNowLowIncomeHint',
    question: 'Is your household income around or below what most families earn in your area?',
    subtitle: 'This helps see if you might fit special refinance programs for moderate incomes.',
    type: 'radio',
    showIf: (a) =>
      a.purchaseOrRefinance === 'refinance' &&
      ['rate-term', 'not-sure', 'limited-cash-out'].includes(a.refiGoal) &&
      a.propertyUse === 'primary' &&
      (a.agencyLoanServicer === 'fannie' || a.agencyLoanServicer === 'freddie'),
    options: [
      { value: 'yes', label: 'Yes — at or below typical for my area', iconClass: 'ti-check' },
      { value: 'maybe', label: 'Maybe / I am not sure', iconClass: 'ti-help' },
      { value: 'no', label: 'No — above typical for my area', iconClass: 'ti-x' },
    ],
    education: {
      title: 'Income in your area',
      description: 'Some refinance programs are aimed at borrowers whose income is near or below the median in their county.',
      details: [
        'A rough guess is fine — this is not a final income test.',
      ],
    },
  },
  {
    id: 'employmentStatus',
    question: 'What best describes how you earn income?',
    type: 'radio',
    options: [
      { value: 'employed-w2', label: 'Employed by a company (paycheck with taxes withheld)', iconClass: 'ti-briefcase' },
      { value: 'self-employed', label: 'Self-employed or own a business', iconClass: 'ti-user-circle' },
      { value: 'retired', label: 'Retired', iconClass: 'ti-beach' },
      { value: 'other', label: 'Other situation', iconClass: 'ti-coin' },
    ],
    education: {
      title: 'How you earn money',
      description: 'Lenders verify income differently for employees, business owners, and retirees.',
      details: [],
    },
  },
  {
    id: 'incomeDocumentation',
    question: 'How could you show your income to a lender?',
    subtitle: 'Pick the option that sounds most like your situation.',
    type: 'radio',
    showIf: (a) =>
      a.employmentStatus === 'self-employed' ||
      a.employmentStatus === 'retired' ||
      a.employmentStatus === 'other',
    options: [
      { value: 'tax-returns', label: 'Tax returns', iconClass: 'ti-file-text' },
      { value: 'bank-statements', label: 'Bank statements (12–24 months)', iconClass: 'ti-report-money' },
      { value: 'assets', label: 'Savings and investments (asset-based)', iconClass: 'ti-coin' },
      { value: 'rental-income', label: 'Rental income from the property', iconClass: 'ti-home-dollar' },
    ],
    education: {
      title: 'Proving income',
      description: 'If you are not a traditional employee, lenders may use tax returns, bank deposits, or other methods.',
      details: [
        'A loan officer will tell you which documents they need — we are narrowing options for now.',
      ],
    },
  },
  {
    id: 'nonQmFlexInterest',
    question: 'Should I include specialty loans for unusual financial situations?',
    subtitle: 'Most people choose “No” unless a loan officer already suggested a specialty path.',
    type: 'radio',
    options: [
      { value: 'yes', label: 'Yes — include if they might apply', iconClass: 'ti-adjustments' },
      { value: 'no', label: 'No — keep it simple', iconClass: null },
    ],
    education: {
      title: 'Specialty loans',
      description: 'These are for borrowers who do not fit typical employee-and-paycheck profiles. They are off by default.',
      details: [],
    },
  },
  {
    id: 'loanTermPreference',
    question: 'How many years would you like to take to pay off the loan?',
    subtitle: 'Longer terms usually mean lower monthly payments but more interest over time.',
    type: 'radio',
    options: [
      { value: '30', label: '30 years', iconClass: 'ti-calendar' },
      { value: '25', label: '25 years', iconClass: 'ti-calendar' },
      { value: '20', label: '20 years', iconClass: 'ti-calendar' },
      { value: '15', label: '15 years', iconClass: 'ti-calendar' },
      { value: '10', label: '10 years', iconClass: 'ti-calendar' },
      { value: 'not-sure', label: 'No preference yet', iconClass: 'ti-help' },
    ],
    education: {
      title: 'Loan length',
      description: 'This is how long you spread out payments. Shorter terms often have higher monthly payments but less total interest.',
      details: [],
    },
  },
  {
    id: 'rateTypePreference',
    question: 'Do you want an interest rate that stays the same, or one that can change later?',
    type: 'radio',
    options: [
      { value: 'fixed', label: 'Stays the same for the life of the loan', iconClass: 'ti-lock' },
      { value: 'arm', label: 'Can change after an initial period', iconClass: 'ti-chart-line' },
      { value: 'not-sure', label: 'Show me both — I am not sure', iconClass: 'ti-help' },
    ],
    education: {
      title: 'Fixed vs adjustable',
      description: 'A fixed rate never changes. An adjustable rate can go up or down after a set number of years.',
      details: [
        'Many first-time buyers prefer a fixed rate for predictability.',
      ],
    },
  },
  {
    id: 'planToKeepLoan',
    question: 'If your rate can change later, how long do you expect to keep this home?',
    subtitle: 'Helps match you with an initial fixed period that fits your plans.',
    type: 'radio',
    showIf: (a) => a.rateTypePreference === 'arm',
    options: [
      { value: '1-3', label: '1–3 years', iconClass: 'ti-clock' },
      { value: '3-5', label: '3–5 years', iconClass: 'ti-clock' },
      { value: '5-7', label: '5–7 years', iconClass: 'ti-clock' },
      { value: '7-10', label: '7–10 years', iconClass: 'ti-clock' },
      { value: '10+', label: '10+ years', iconClass: 'ti-clock' },
    ],
    education: {
      title: 'How long you will stay',
      description: 'If you may sell or refinance soon, a shorter initial fixed period on an adjustable loan may be worth discussing.',
      details: [],
    },
  },
  {
    id: 'refinanceType',
    question: 'What are you trying to do with your VA loan?',
    subtitle: 'Only shown if you indicated military service and are refinancing.',
    type: 'radio',
    showIf: (a) => a.militaryService === 'yes' && a.purchaseOrRefinance === 'refinance',
    options: [
      { value: 'va-to-va', label: 'Refinance my existing VA loan to a lower rate', iconClass: 'ti-bolt' },
      { value: 'non-va-to-va', label: 'Switch a non-VA loan to a VA loan', iconClass: 'ti-switch' },
      { value: 'cash-out', label: 'Take cash out of my equity with a VA loan', iconClass: 'ti-cash' },
    ],
    education: {
      title: 'VA refinance types',
      description: 'VA loans have different refinance paths depending on whether you already have a VA mortgage and whether you want cash back.',
      details: [],
    },
  },
  {
    id: 'annualIncome',
    question: 'What is your total yearly income before taxes?',
    subtitle: 'Include wages and other regular income you expect a lender to count.',
    type: 'currency',
    placeholder: '120,000',
    education: {
      title: 'Annual income',
      description: 'Use your gross income (before taxes). A reasonable estimate helps me explain how much home payment might fit your budget.',
      details: [],
    },
  },
  {
    id: 'monthlyDebts',
    question: 'About how much do you pay each month on debts (not counting this new housing payment)?',
    subtitle: 'Include car loans, credit cards, student loans, and similar bills.',
    type: 'currency',
    placeholder: '800',
    education: {
      title: 'Monthly debts',
      description: 'Lenders compare your debt payments to your income. Do not include rent or your future mortgage payment here.',
      details: [],
    },
  },
];
